/**
 * The operations shell: a fixed rail, one page measure, and the role line every screen shares.
 * Labels stay in the rail. The page does not scroll sideways.
 */
import * as stylex from "@stylexjs/stylex";
import type { FormEvent, MouseEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import type { StaffRole } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Mark } from "@lattice/ui/Mark";
import type { Me } from "./api.ts";
import { Avatar, Reveal } from "./bits.tsx";
import { useNotice } from "./notice.ts";
import { href, navigate, useScreen } from "./router.ts";
import type { Screen } from "./router.ts";
import { AddPasskey } from "./SignIn.tsx";
import { RoleProvider } from "./staff.ts";
import { Button } from "./ui.tsx";
import { STAFF_ROLE } from "./words.ts";

/* Fixed column so nav labels cannot wrap into the page. Not a padding. */
const RAIL = "15rem";

/* Reading measure beside the rail. Not a padding. */
const MEASURE = "68rem";
interface NavItem {
  readonly id: string;
  readonly label: string;
  readonly to: string;
}
const WORK: readonly NavItem[] = [{
  id: "funnel",
  label: "Funnel",
  to: href.funnel(null)
}, {
  id: "reviews",
  label: "Reviews",
  to: href.reviews()
}, {
  id: "jobs",
  label: "Jobs",
  to: href.jobs()
}, {
  id: "orders",
  label: "Orders",
  to: href.orders()
}, {
  id: "reactions",
  label: "Reactions",
  to: href.reactions()
}, {
  id: "audit",
  label: "Audit",
  to: href.audit()
}];
const ENGINE: readonly NavItem[] = [{
  id: "catalog",
  label: "Catalog",
  to: href.catalog()
}, {
  id: "rules",
  label: "Rules",
  to: href.rules()
}, {
  id: "evaluation",
  label: "Evaluation",
  to: href.evaluation()
}];
function showItem(role: StaffRole, id: string): boolean {
  if (id === "catalog" || id === "rules" || id === "evaluation") return role === "engine" || role === "superuser";
  if (role === "engine") return false;
  if (id === "reviews") return role === "clinician" || role === "superuser";
  if (id === "orders") return role !== "clinician";
  return true;
}
function isCurrent(screen: Screen, id: string): boolean {
  switch (id) {
    case "funnel":
      return screen.kind === "funnel";
    case "reviews":
      return screen.kind === "reviews" || screen.kind === "review";
    case "jobs":
      return screen.kind === "jobs";
    case "orders":
      return screen.kind === "orders" || screen.kind === "pharmacyOrder";
    case "reactions":
      return screen.kind === "reactions";
    case "audit":
      return screen.kind === "audit";
    case "catalog":
      return screen.kind === "catalog";
    case "rules":
      return screen.kind === "rules";
    case "evaluation":
      return screen.kind === "evaluation";
    default:
      return false;
  }
}
function follow(event: MouseEvent, to: string) {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  event.preventDefault();
  navigate(to);
}
function NavGroup({
  label,
  items,
  role,
  screen
}: {
  readonly label: string;
  readonly items: readonly NavItem[];
  readonly role: StaffRole;
  readonly screen: Screen;
}) {
  const visible = items.filter(item => showItem(role, item.id));
  if (visible.length === 0) return null;
  return <div className="x78zum5 xdt5ytf xskm3c9">
      <div className="x78zum5 x6s0dn4 xskm3c9 xxi9w3g xc7sml1 x1jty25b xl4dl4e xqu410z">{label}</div>
      {visible.map(item => {
      const current = isCurrent(screen, item.id);
      return <a key={item.id} href={item.to} aria-current={current ? "page" : undefined} onClick={event => follow(event, item.to)} {...{
        0: {
          className: "x78zum5 x6s0dn4 xxi9w3g xerji9v x1fbwzx xzb3lse xqu410z x1hl2dhg xuxw1ft xb3r6kr xlyipyv x171263r x784prv x9v5kkp x1jubnyg x1bqaal"
        },
        1: {
          className: "x78zum5 x6s0dn4 xxi9w3g xerji9v x1fbwzx xqu410z x1hl2dhg xuxw1ft xb3r6kr xlyipyv x171263r x784prv x9v5kkp x1jubnyg x1bqaal x1890um5 x10dbofl"
        }
      }[!!current << 0]}>
            {item.label}
          </a>;
    })}
    </div>;
}
function RailSearch({
  screen
}: {
  readonly screen: Screen;
}) {
  const current = screen.kind === "search" ? screen.query : "";
  const [draft, setDraft] = useState<{
    readonly for: string;
    readonly value: string;
  }>({
    for: current,
    value: current
  });
  const value = draft.for === current ? draft.value : current;
  function submit(event: FormEvent) {
    event.preventDefault();
    navigate(href.search(value.trim()));
  }
  return <form role="search" onSubmit={submit} className="x78zum5 xdt5ytf x1173whf">
      <input type="search" aria-label="Search accounts, patient records and clinics" placeholder="Name, email or id" value={value} onChange={event => setDraft({
      for: current,
      value: event.target.value
    })} autoFocus={screen.kind === "search" && current === ""} className="x9f619 xh8yej3 xeuugli xxi9w3g xerji9v x1cikjda xl4dl4e xzb3lse x1l0w1cl xmkeg23 x1y0btm7 xtkjqb x1fbwzx x784prv x9v5kkp x1jubnyg x7s97pk" />
      <Button type="submit" tone="quiet" fill>
        Search
      </Button>
    </form>;
}
function Rail({
  me,
  onSignOut
}: {
  readonly me: Me;
  readonly onSignOut: () => void;
}) {
  const screen = useScreen();
  const role = me.staffRole;
  if (role === null) return null;
  const email = me.email ?? "Signed in";
  return <aside className="xixxii4 xhtitgo x10no89f x1o0tod x78zum5 xdt5ytf x9ndnb8 x9f619 xcqm4px x1lun4ml x18b5jzi xms87nr">
      <a href="/" onClick={event => follow(event, "/")} className="x78zum5 x6s0dn4 x1173whf x2lah0s xxi9w3g x148gad2 xc7sml1 x10dbofl x117lpx5 x1rf3zrh x1hl2dhg x8wwnw1 x784prv x9v5kkp x1jubnyg x7s97pk">
        <Mark size={20} />
        <span className="xzb3lse">Lattice</span>
        <span className="x1jty25b xox936i">Admin</span>
      </a>
      <nav aria-label="Admin" className="x12lumcd x2lwn1j x1odjw0f x78zum5 xdt5ytf x1r0x45p xerji9v x42188t">
        {role === "engine" ? null : <RailSearch screen={screen} />}
        <NavGroup label="Work" items={WORK} role={role} screen={screen} />
        <NavGroup label="Engine" items={ENGINE} role={role} screen={screen} />
      </nav>
      <div className="x78zum5 xdt5ytf x1173whf x2lah0s x1a91ssu x178xt8z x13fuv20 xez8t42">
        <div className="x78zum5 x6s0dn4 x1173whf xeuugli">
          <Avatar name={null} email={me.email} large />
          <div className="x78zum5 xdt5ytf xeuugli x12lumcd">
            <Reveal value={email} />
            <span className="x1jty25b xuxw1ft">{STAFF_ROLE[role]}</span>
          </div>
        </div>
        <AddPasskey label="Add passkey" fill />
        <Button tone="quiet" fill onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </aside>;
}
function NoticeBar() {
  const notice = useNotice();
  const ref = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (notice !== null) ref.current?.scrollIntoView({
      block: "nearest"
    });
  }, [notice]);
  if (notice === null) return null;
  return <p ref={ref} role="status" {...{
    0: {
      className: "x7wzq59 x13vifvy xzkaem6 x1ghz6dp x1jyo4x1 xerji9v x1fbwzx xmkeg23 x1y0btm7 xtkjqb xcqm4px xqu410z x15406qy x149xavy xa02eh3"
    },
    2: {
      className: "x7wzq59 x13vifvy xzkaem6 x1ghz6dp x1jyo4x1 xerji9v x1fbwzx xmkeg23 x1y0btm7 xtkjqb xcqm4px xqu410z x15406qy x149xavy xa02eh3 x1p2vmyc"
    },
    1: {
      className: "x7wzq59 x13vifvy xzkaem6 x1ghz6dp x1jyo4x1 xerji9v x1fbwzx xmkeg23 x1y0btm7 xtkjqb xqu410z x15406qy x149xavy xa02eh3 x156x1qg xcqm4px"
    },
    3: {
      className: "x7wzq59 x13vifvy xzkaem6 x1ghz6dp x1jyo4x1 xerji9v x1fbwzx xmkeg23 x1y0btm7 xtkjqb xqu410z x15406qy x149xavy xa02eh3 x156x1qg xcqm4px"
    }
  }[!!(notice.tone === "saved") << 1 | !!(notice.tone === "failed") << 0]}>
      {notice.text}
    </p>;
}
export function Shell({
  me,
  onSignOut,
  children
}: {
  readonly me: Me;
  readonly onSignOut: () => void;
  readonly children: ReactNode;
}) {
  return <RoleProvider role={me.staffRole}>
      <div className="x1ov3xa9 x1l0w1cl xzb3lse x1cikjda xl4dl4e xbz0q3o">
        <Rail me={me} onSignOut={onSignOut} />
        <div className="x1d101q3 xeuugli x1ov3xa9 x101abm8">
          <main className="x9f619 xh8yej3 xzsb0xr xvueqy4 xq24xx7 xcmmui4 x78zum5 xdt5ytf x15agyyk xeuugli">
            <NoticeBar />
            {children}
          </main>
        </div>
      </div>
    </RoleProvider>;
}